/**
 * metaToWeb — adapter that turns the Meta /api/meta/insights response
 * into the node/edge graph shape consumed by TheWebPage / WebGraph.
 *
 * The mapping today (without an AI tagging engine yet, outline §4):
 *
 *   creative                     → concept node
 *     spend = sum of variant spend
 *     roas  = weighted creative-level ROAS
 *     status: derived from spend + activity in the underlying ads
 *
 *   ad (per creative)            → variation node, parented to the creative
 *     status: from effective_status + spend + ROAS
 *     scaling: high-spend + high-ROAS heuristic
 *
 * Tags we cannot infer without AI yet are left blank — the graph still
 * encodes status/spend/roas/ctr/hookRate, which is enough for the visual
 * to be useful from day 1. funnel / awareness / angle filters will simply
 * not match those nodes (they'll show in "all" view).
 */

const SCALE_SPEND_THRESHOLD = 5000;
const SCALE_ROAS_THRESHOLD = 3;
const KILL_SPEND_THRESHOLD = 200;       // below this = no signal yet
const KILL_ROAS_THRESHOLD = 1.0;        // 1x or below = killed

const slug = (id) => `meta_${String(id).replace(/[^a-zA-Z0-9_-]/g, "_")}`;

/**
 * labelize — turn a raw Meta ad/creative name into a human label.
 *
 * Meta ad names in the wild look like:
 *   "{{product.name}} 2026-02-07-4eb47d971392e5808bc..."
 *   "Manmade_10 2026-04-13-1e86f802eba0240e785cdd7af..."
 *   "Trim Your Hair 2026-03-26-8243a9b6c..."
 *
 * Strategy:
 *   1. Drop unresolved Liquid placeholders ({{product.name}}, {{ad.name}}, …).
 *   2. Drop ISO date stamps (yyyy-mm-dd) and any hex hash >=8 chars that
 *      typically tail an asset filename.
 *   3. Drop standalone version tags ("v1", "001") that survived the hash strip.
 *   4. Replace _ - with spaces, collapse whitespace, title-case obvious tokens.
 *   5. Truncate ~48 chars with ellipsis.
 *   6. If the resulting string is empty, return the fallback.
 */
const LIQUID_RE = /\{\{[^}]*\}\}/g;
const DATE_RE = /\b\d{4}-\d{2}-\d{2}(?:-\w+)?/g;
const HASH_RE = /\b[0-9a-f]{8,}\b/gi;
const VERSION_TAG_RE = /\b(?:v\d+|\d{3,})\b/gi;

const labelize = (raw, fallback = "Untitled creative") => {
  const rawTrim = String(raw || "").trim();
  if (!rawTrim) return fallback;

  let s = rawTrim
    .replace(LIQUID_RE, "")
    .replace(DATE_RE, "")
    .replace(HASH_RE, "")
    .replace(VERSION_TAG_RE, "")
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Drop trailing punctuation noise left behind ("Ad name —" / "Ad name |")
  s = s.replace(/[\s|·•—–-]+$/g, "").trim();

  // If aggressive stripping removed everything but the source had real text,
  // fall back to a cleaned version of the raw input rather than the generic
  // "Creative <id>" placeholder. Better to show "Manmade 10" than to hide it.
  if (!s) {
    const lenient = rawTrim
      .replace(LIQUID_RE, "")
      .replace(/[._-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (lenient && lenient.length > 1) {
      s = lenient;
    } else {
      return fallback;
    }
  }

  // Title-case ALL-CAPS or lowercase tokens; leave mixed-case ("UGC", "PMAX") alone
  s = s
    .split(" ")
    .map((tok) => {
      if (!tok) return tok;
      if (/[A-Z]/.test(tok) && /[a-z]/.test(tok)) return tok; // mixed-case, keep
      if (tok === tok.toUpperCase() && tok.length <= 4) return tok; // acronym
      return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();
    })
    .join(" ");

  return s.length > 48 ? `${s.slice(0, 45)}…` : s;
};

const deriveStatus = ({ spend, roas, effective_status }) => {
  const status = String(effective_status || "").toUpperCase();
  if (spend < KILL_SPEND_THRESHOLD) {
    if (status === "ACTIVE") return "active"; // active but too early to judge
    return "paused";
  }
  if (roas <= KILL_ROAS_THRESHOLD) return "killed";
  if (status === "PAUSED" || status === "DISAPPROVED" || status === "CAMPAIGN_PAUSED") {
    return "paused";
  }
  if (spend >= SCALE_SPEND_THRESHOLD && roas >= SCALE_ROAS_THRESHOLD) return "scaling";
  return "active";
};

const deriveConfidence = (spend) => {
  // Simple log-curve: $50 → ~0.2, $500 → ~0.55, $5k → ~0.85, $50k → ~1
  if (spend <= 0) return 0;
  const v = Math.log10(spend + 1) / 5; // 5 = log10(100k)
  return Math.max(0, Math.min(1, v));
};

const guessFormat = (creative) => {
  const oss = creative?.object_story_spec || {};
  if (oss?.video_data) return "video_hook";
  if (oss?.link_data?.child_attachments?.length > 1) return "carousel";
  if (oss?.template_data) return "advertorial";
  return "static_ugc";
};

/* -------------------------------------------------------------------- */
/*  Public: convert Meta payload → { nodes, edges, summary }            */
/* -------------------------------------------------------------------- */

export function metaToWeb(payload, options = {}) {
  const ads = Array.isArray(payload?.ads) ? payload.ads : [];
  const creatives = Array.isArray(payload?.creatives) ? payload.creatives : [];
  const analysisIndex = options.analysisIndex || null;
  const lookupAnalysis = (creativeId, adId) => {
    if (!analysisIndex) return null;
    if (adId && analysisIndex.byAd?.has?.(adId)) {
      return analysisIndex.byAd.get(adId);
    }
    if (creativeId && analysisIndex.byCreative?.has?.(creativeId)) {
      return analysisIndex.byCreative.get(creativeId);
    }
    return null;
  };

  const conceptIdByCreative = new Map();
  const nodes = [];
  const edges = [];

  // A concept (creative) is "active" if any of its underlying ads is ACTIVE.
  const activeByCreative = new Map();
  ads.forEach((a) => {
    if (!a.creative_id) return;
    const isActive = String(a.effective_status || "").toUpperCase() === "ACTIVE";
    activeByCreative.set(
      a.creative_id,
      (activeByCreative.get(a.creative_id) || false) || isActive,
    );
  });

  // Build concept nodes (one per creative_id that has any ads)
  creatives.forEach((c) => {
    const m = c.metrics || {};
    const id = slug(c.creative_id);
    conceptIdByCreative.set(c.creative_id, id);

    const a = lookupAnalysis(c.creative_id, null);
    const aiLabel = a?.concept_label && String(a.concept_label).trim();

    nodes.push({
      id,
      type: "concept",
      label: aiLabel || labelize(c.creative_name, `Creative ${c.creative_id}`),
      analysis: a?.analysis || null,
      citations: a?.citations || null,
      transcript: a?.transcript || null,
      parent_concept_id: a?.parent_concept_id || null,
      video_id: c.video_id || "",
      retention: c.retention || null,
      level: 1,
      funnel: null,
      awareness: null,
      sophistication: null,
      angle: null,
      format: null,
      status: deriveStatus({
        spend: m.spend || 0,
        roas: m.roas || 0,
        effective_status: activeByCreative.get(c.creative_id)
          ? "ACTIVE"
          : "PAUSED",
      }),
      spend: Math.round(m.spend || 0),
      roas: Number((m.roas || 0).toFixed(2)),
      ctr: Number((m.ctr || 0).toFixed(2)),
      hookRate: Number((m.hookRate || 0).toFixed(3)),
      confidence: deriveConfidence(m.spend || 0),
      scaling:
        (m.spend || 0) >= SCALE_SPEND_THRESHOLD &&
        (m.roas || 0) >= SCALE_ROAS_THRESHOLD,
      thumbnail: c.thumbnail_url || "",
      _meta: {
        creative_id: c.creative_id,
        creative_name: c.creative_name || "",
        ads: c.ads,
      },
    });
  });

  // Build variation nodes (one per ad), parent → concept (creative)
  ads.forEach((a) => {
    const m = a.metrics || {};
    const conceptId = a.creative_id ? conceptIdByCreative.get(a.creative_id) : null;
    const id = slug(a.ad_id);

    // If the ad has no creative_id (rare), promote it to a standalone concept
    const parentId = conceptId || null;

    const ax = lookupAnalysis(a.creative_id, a.ad_id);
    const aiLabel = ax?.concept_label && String(ax.concept_label).trim();

    nodes.push({
      id,
      type: parentId ? "variation" : "concept",
      label: aiLabel || labelize(a.ad_name, `Ad ${a.ad_id}`),
      analysis: ax?.analysis || null,
      citations: ax?.citations || null,
      transcript: ax?.transcript || null,
      parent_concept_id: ax?.parent_concept_id || null,
      video_id: a.video_id || "",
      retention: a.retention || null,
      level: 1,
      parent: parentId,
      funnel: null,
      awareness: null,
      angle: null,
      format: guessFormat({ object_story_spec: a.object_story_spec }),
      status: deriveStatus({
        spend: m.spend || 0,
        roas: m.roas || 0,
        effective_status: a.effective_status,
      }),
      spend: Math.round(m.spend || 0),
      roas: Number((m.roas || 0).toFixed(2)),
      ctr: Number((m.ctr || 0).toFixed(2)),
      hookRate: Number((m.hookRate || 0).toFixed(3)),
      confidence: deriveConfidence(m.spend || 0),
      scaling:
        (m.spend || 0) >= SCALE_SPEND_THRESHOLD &&
        (m.roas || 0) >= SCALE_ROAS_THRESHOLD,
      thumbnail: a.thumbnail_url || "",
      _meta: {
        ad_id: a.ad_id,
        creative_id: a.creative_id,
        ad_name: a.ad_name || "",
        effective_status: a.effective_status || null,
      },
    });

    if (parentId) {
      edges.push({ source: parentId, target: id, type: "parent" });
    }
  });

  return {
    nodes,
    edges,
    summary: payload?.summary || null,
    range: payload?.range || null,
  };
}

/* -------------------------------------------------------------------- */
/*  Daily card derivation from live data (outline §8.1, lite version)   */
/* -------------------------------------------------------------------- */

export function deriveDailyCards({ nodes }) {
  const cards = [];

  // Top scaler
  const topScale = nodes
    .filter((n) => n.type === "variation" && n.scaling)
    .sort((a, b) => b.roas - a.roas)[0];
  if (topScale) {
    cards.push({
      id: `card_scale_${topScale.id}`,
      kind: "scale",
      title: `${topScale.label} crossed scale-threshold`,
      body: `ROAS ${topScale.roas.toFixed(1)} on $${topScale.spend.toLocaleString()} spend — recommend pushing daily cap.`,
      nodeId: topScale.id,
      actions: ["Scale", "Defer"],
    });
  }

  // Worst killer (active spend, ROAS below 1)
  const worstKill = nodes
    .filter((n) => n.status === "killed" && n.spend > 0)
    .sort((a, b) => b.spend - a.spend)[0];
  if (worstKill) {
    cards.push({
      id: `card_kill_${worstKill.id}`,
      kind: "kill",
      title: `${worstKill.label} underperforming`,
      body: `$${worstKill.spend.toLocaleString()} spent at ${worstKill.roas.toFixed(1)} ROAS — recommend kill.`,
      nodeId: worstKill.id,
      actions: ["Kill", "Defer"],
    });
  }

  // Highest hook-rate variation that hasn't scaled yet → potential winner
  const sleeper = nodes
    .filter(
      (n) =>
        n.type === "variation" &&
        !n.scaling &&
        n.hookRate > 0.3 &&
        n.spend > 200,
    )
    .sort((a, b) => b.hookRate - a.hookRate)[0];
  if (sleeper) {
    cards.push({
      id: `card_revive_${sleeper.id}`,
      kind: "revive",
      title: `Hook landing — ${sleeper.label}`,
      body: `Hook rate ${Math.round(sleeper.hookRate * 100)}% but spend only $${sleeper.spend.toLocaleString()}. Test with more budget?`,
      nodeId: sleeper.id,
      actions: ["Brief", "Defer"],
    });
  }

  return cards;
}
